import * as React from 'react';
import {
  Button,
  Tooltip,
  GDSStyleObject,
  Icon,
} from '@leagueplatform/genesis-core';
import { Link } from 'react-router-dom';

export type IconButtonProps = {
  onClick?: () => void;
  icon: string | React.ReactNode;
  hideLabel?: boolean;
  /** The button's accessible label (used for aria-label, and as tooltip text when hideLabel is true) */
  label: string;
  /** Optional tooltip text. When provided, always shows tooltip. When omitted, tooltip only shows if hideLabel is true. */
  tooltip?: string;
  size?: 'large' | 'medium' | 'small' | 'toolbar';
  css?: GDSStyleObject;
  disabled?: boolean;
  priority?: 'primary' | 'secondary' | 'tertiary';
  destructive?: boolean;
  quiet?: boolean;
  iconSize?: string | number;
  className?: string;
  ariaExpanded?: boolean;
  /** Optional route path. When provided, the button renders as a Link for client-side navigation. */
  to?: string;
} & Omit<React.HTMLAttributes<HTMLElement>, 'css'>;

/**
 * These values mirror Genesis Button's internal styling for iconOnly buttons.
 * We need to manually apply them when using a ReactNode icon because Genesis's
 * `hideLabel` prop only works with string icons (it would hide the ReactNode child).
 *
 * If Genesis updates these values, we'll need to update them here as well.
 * @see Genesis Button component for source values
 */
const iconOnlyPadding = {
  large: '$threeQuarters',
  medium: '$half',
  small: '$half',
  toolbar: '$quarter',
} as const;

const iconSizes = {
  large: 24,
  medium: 20,
  small: 16,
  toolbar: 16,
} as const;

/**
 * An icon button with an integrated tooltip for additional context.
 *
 * - `label`: Required. Used as the button's `aria-label`
 * - `tooltip`: Optional. When provided, shows this text in a tooltip on hover
 * - `hideLabel`: When true (default), the label is visually hidden and shown as tooltip on hover.
 *               When false, shows `label` as visible text alongside the icon (no tooltip unless explicitly provided)
 *
 * When the `to` prop is provided, the button renders as a Link for client-side navigation.
 */
export const IconButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  IconButtonProps
>(
  (
    {
      label,
      hideLabel = true,
      tooltip,
      onClick,
      icon,
      css,
      size = 'medium',
      priority = 'primary',
      disabled = false,
      quiet = false,
      destructive = false,
      className,
      ariaExpanded,
      iconSize,
      to,
      ...restProps
    },
    ref,
  ) => {
    // Use separate internal refs - the correct one is used based on `to`
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const linkRef = React.useRef<HTMLAnchorElement>(null);

    // Forward the appropriate internal ref to the external ref
    React.useImperativeHandle(ref, () =>
      to ? linkRef.current! : buttonRef.current!,
    );

    // When icon is a ReactNode (not a string), we can't use Genesis's hideLabel prop because it would hide the icon child. Instead, we manually apply the iconOnly to make the button a square
    const isReactNodeIcon = typeof icon !== 'string';
    const iconOnlyCss =
      isReactNodeIcon && hideLabel ? { padding: iconOnlyPadding[size] } : {};
    // Use the provided iconSize or fall back to Genesis's default for the button size
    const resolvedIconSize = iconSize ?? iconSizes[size];
    // Show tooltip if explicitly provided, or if label is hidden (icon-only buttons need tooltip for discoverability)
    const showTooltip = tooltip !== undefined || hideLabel;
    const resolvedTooltip = tooltip ?? label;

    const buttonContent = (
      <>
        {!hideLabel && label}
        {isReactNodeIcon && (
          <Icon
            icon={icon}
            size={resolvedIconSize}
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        )}
      </>
    );

    const sharedButtonProps = {
      onClick,
      size,
      priority,
      quiet,
      destructive,
      disabled,
      css: { ...iconOnlyCss, ...css },
      'aria-label': label,
      className,
      icon: typeof icon === 'string' ? icon : undefined,
      hideLabel: typeof icon === 'string' ? hideLabel : undefined,
      ...(ariaExpanded !== undefined && { 'aria-expanded': ariaExpanded }),
      ...restProps,
    };

    const buttonElement = to ? (
      <Button ref={linkRef} as={Link} to={to} {...sharedButtonProps}>
        {buttonContent}
      </Button>
    ) : (
      <Button ref={buttonRef} {...sharedButtonProps}>
        {buttonContent}
      </Button>
    );

    if (!showTooltip) {
      return buttonElement;
    }

    return (
      <Tooltip
        align="center"
        avoidCollisions
        content={resolvedTooltip}
        side="bottom"
        css={{
          // higher z index than $modal so the tooltip is always on top of the modal when buttons are rendered in a modal
          zIndex: '$toast',
        }}
      >
        {buttonElement}
      </Tooltip>
    );
  },
);

IconButton.displayName = 'IconButton';
